import React, { useState, useEffect, useRef, useCallback } from "react";
import config from "../../config";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";
import NoDataFound from '../../shared/NoDataFound';
import { useTableState } from "./utils/useTableState"
import { usePersistedFilterState } from "./utils/usePersistedFilterState"
import { alphaformatDateTime } from "../../utils/dateFormat"
import TableLoader from "../../shared/TableLoader"
import FilterBar from './components/FilterBar';
import ContentWriterAssignmentModal from './components/ContentWriterAssignmentModal';
import ContentDrawer from "./components/ContentDrawer";
import LocationDetails from "./components/LocationDetails";
import { EmitEvent } from "../../services/socket";
import { renderSalesPersonCell, renderDesignStatusCell } from "./utils/cellRenderers";
import CreativeName from "./components/CreativeName";
import MessageIcon from "./components/MessageIcon";
import { useUnreadCountPolling } from "./utils/useUnreadCountPolling";
import { useVisibleRowIds } from "./utils/useVisibleRowIds";

const STATUS_OPTIONS = [
    { value: 2, label: 'Saved' },
];
export const types = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "full_page",
    label: "Full Image",
  },
  {
    value: "full_video",
    label: "Full Video",
  },
  {
      value:"half_video",
      label:"Half Video"
  },
  {
    value: "promoted_image_story",
    label: "Promoted Image Story ",
  },
  {
    value:"promoted_video_story",
    label:"Promoted Video Story"
  },
  {
    value: "classifiedads",
    label: "Classified Ads"
  }
]
const AssignToDesignerAds = () => {
  const [Designer, setDesigners] = useState([]);
  const [assignAds, setAssignAds] = useState([]);
  const [loader, setloader] = useState(false);
  const [designerAssignments, setDesignerAssignments] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const { currentPage, setCurrentPage, contentWriters, fetchContentWriters, getSalesList, salesList, fetchSalesLocations, salesLocations } = useTableState();
  const { visibleIds, rowRef } = useVisibleRowIds();
  const liveUnread = useUnreadCountPolling(assignAds, "/alpha_cst_designer/getUnreadCounts", { visibleIds });
  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const loadMoreCtxRef = useRef({});
  const [showAdDetails, setShowAdDetails] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);;
  const [rowData,setRowData]=useState({})
  // Content writer selection per creative and pick flow modal
  const [contentWriterAssignments, setContentWriterAssignments] = useState({});
  const [pickModalOpen, setPickModalOpen] = useState(false);
  const [pickModalContext, setPickModalContext] = useState({ creative_id: null, content_id: null, cw: null, designer: null });

  // Filter state
  // const today = new Date().toISOString().split('T')[0];
  // const tomorrow = new Date();
  // tomorrow.setDate(tomorrow.getDate() + 1);
  // const tomorrowStr = tomorrow.toISOString().split('T')[0];
  const [dateRange, setDateRange] = usePersistedFilterState('alpha_designer.unpicked_ads.assign_to_designer.dateRange', { startDate: null, endDate: null });
  const [status, setStatus] = useState(STATUS_OPTIONS[0]); // Always 2 (Saved)
  const [adType, setAdType] = usePersistedFilterState('alpha_designer.unpicked_ads.assign_to_designer.adType', null);
  const [search, setSearch] = usePersistedFilterState('alpha_designer.unpicked_ads.assign_to_designer.search', '');
  const [salesPerson, setSalesPerson] = usePersistedFilterState('alpha_designer.unpicked_ads.assign_to_designer.salesPerson', null);
  const [salesLocation, setSalesLocation] = usePersistedFilterState('alpha_designer.unpicked_ads.assign_to_designer.salesLocation', []);

  const AssignToDesignData = async (page, append = false) => {
    try {
      setloader(true);
      let res = await api({
        url: `/alpha_cst_designer/getDesignes`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          status: status ? status.value : 2,
          adType: adType && adType.value !== 'all' ? adType.value : null,
          search: search || null,
          sales_person_id: salesPerson?.value ? [salesPerson.value] : null,
          sales_location: salesLocation?.length ? salesLocation.map((o) => o.value) : null,
        }
      });
      if (res.code === 200) {
        const newData = res.data?.data || [];
        const total = res.data?.total || 0;
        if (append) {
          setAssignAds(prev => [...prev, ...newData]);
        } else {
          setAssignAds(newData);
        }
        const totalPgs = Math.ceil(total / config.PER_PAGE);
        setHasMore((page + 1) < totalPgs);
        setTotalRecords(total);
      }
      setloader(false);
    } catch (error) {
      setloader(false);
      console.error(error);
    }
  }

  const FetchDesigners = async () => {
    try {
      let res = await api({
        url: `/alpha_cst_designer/getDesigners`,
        method: "POST",
        responseType: "json",
        data: {}
      });
      if (res.code === 200)
        setDesigners(res.data?.data);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    FetchDesigners();
    fetchContentWriters();
    getSalesList();
    fetchSalesLocations();
    setCurrentPage(0);
  }, []);

  // Update ref for load more
  loadMoreCtxRef.current = { loader, hasMore, currentPage, dateRange, status, adType, search, salesPerson, salesLocation };

  // Reset and fetch on filter change
  useEffect(() => {
    setCurrentPage(0);
    setAssignAds([]);
    AssignToDesignData(0, false);
  }, [dateRange, status, adType, search, salesPerson, salesLocation]);

  const handleLoadMore = useCallback(() => {
    const ctx = loadMoreCtxRef.current;
    if (ctx.loader || !ctx.hasMore) return;
    const nextPage = ctx.currentPage + 1;
    setCurrentPage(nextPage);
    AssignToDesignData(nextPage, true);
  }, []);

  useEffect(() => {
    const sentinels = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!sentinels.length) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) handleLoadMore(); },
      { threshold: 0.1 }
    );
    sentinels.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore, assignAds.length]);

  const handleDateChange = (val) => setDateRange(val);
  const handleStatusChange = (val) => setStatus(val);
  const handleAdTypeChange = (val) => setAdType(val);
  const handleSearchChange = (val) => setSearch(val);
  const handleSalesPersonChange = (val) => setSalesPerson(val);
  const handleSalesLocationChange = (val) => setSalesLocation(val);

  const handlePick = async ({ creative_id, designer_id, designer_name, content_id = null }) => {
    try {
      if (!designer_id) {
        Toast('warning', "Please select a designer.");
        return;
      }
      let res = await api({
        url: `/alpha_cst_designer/assigntoDesigner`,
        method: "POST",
        responseType: "json",
        data: {
          creative_id,
          designer_id,
          designer_name,
        }
      });
      if (res.code === 200) {
        Toast('success', "Designer assigned succesfully");
        EmitEvent(creative_id,res.data?.log[0])
        AssignToDesignData(currentPage)
      }
      else {
        Toast('error', res.data?.message)
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handlePickClick = (row) => {
    const creative_id = row.creative_id;
    const designerSel = designerAssignments[creative_id] ? JSON.parse(designerAssignments[creative_id]) : {};
    if (!designerSel?.id) {
      Toast('warning', 'Please select a designer.');
      return;
    }
    const cwSel = contentWriterAssignments[creative_id] ? JSON.parse(contentWriterAssignments[creative_id]) : null;
    if (cwSel?.id) {
      // Ask only for description and then proceed
      setPickModalContext({
        creative_id,
        content_id: row.content_id || null,
        cw: { id: cwSel.id, name: cwSel.name },
        designer: { id: designerSel.id, name: designerSel.name }
      });
      setPickModalOpen(true);
      return;
    }
    // No content writer selected; pick ad directly
    handlePick({ creative_id, designer_id: designerSel.id, designer_name: designerSel.name });
  };

  const handleConfirmPickWithDescription = async ({ creative_id, content_id, description }) => {
    try {
      const cw = pickModalContext.cw;
      if (cw?.id) {
        const res = await api({
          url: `/alpha_cst_designer/contentWriter`,
          method: 'POST',
          responseType: 'json',
          data: {
            content_id: content_id || null,
            creative_id,
            content_writer_id: cw.id,
            content_writer_name: cw.name,
            description: description || ''
          }
        });
        if(res.status==='SUCCESS'){
           Toast('success', 'Content writer assigned successfully');
           EmitEvent(creative_id,res.data?.log[0])
        }
      }
    } catch (err) {
      Toast('error', 'Failed to assign content writer. Proceeding to pick designer.');
    }
    const designer = pickModalContext.designer;
    await handlePick({ creative_id, designer_id: designer.id, designer_name: designer.name });
    setPickModalOpen(false);
    setPickModalContext({ creative_id: null, content_id: null, cw: null, designer: null });
  };

  return (
    <div>
      <FilterBar
        dateRange={dateRange}
        onDateChange={handleDateChange}
        showDesigner={false}
        status={status}
        onStatusChange={handleStatusChange}
        statusOptions={STATUS_OPTIONS}
        showStatus={false}
        adType={adType}
        onAdTypeChange={handleAdTypeChange}
        adTypeOptions={types}
        showAdType={true}
        search={search}
        onSearchChange={handleSearchChange}
        showSearch={true}
        salesPerson={salesPerson}
        onSalesPersonChange={handleSalesPersonChange}
        salesPersonOptions={salesList}
        showSalesPerson={true}
        salesLocation={salesLocation}
        onSalesLocationChange={handleSalesLocationChange}
        showSalesLocation={true}
        salesLocationOptions={salesLocations}
      />
      <div className='flex flex-col items-start justify-center gap-2'>
        <div className="px-2 py-1 shadow-md rounded-lg bg-gray-300">
            <span>Total Ads: {totalRecords}</span>
        </div>

        {/* ---- Mobile-only card view (<=767px) ---- */}
        <div className="lineitems-mobile-view w-full">
          {loader && assignAds.length === 0 ? (
            <div className="text-center text-gray-500 py-6 italic">Loading...</div>
          ) : assignAds?.length > 0 ? (
            assignAds.map((rawRow, idx) => {
              const liveCount = liveUnread[rawRow.creative_id];
              const row = liveCount != null ? { ...rawRow, unread_count: liveCount, unreadCount: liveCount } : rawRow;
              const created = alphaformatDateTime(row.created_at);
              const start = alphaformatDateTime(row.creative_start_date);
              const end = alphaformatDateTime(row.creative_start_date, 'end');
              const sp = row.sales_person || {};
              const initials = (sp.sales_person_name || '?')
                .trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
              const isPackage = row.creative_type === 'PACKAGE' && row.package_weeks;
              const packageIndex = isPackage && row.package_position ? row.package_position : null;
              return (
                <div key={row.creative_id} ref={rowRef(rawRow.creative_id, 'mobile')} className="reqcard">
                  <div className="reqcard__head">
                    <div className="reqcard__head-left">
                      <span className="reqcard__sno">#{String(idx + 1).padStart(2, '0')}</span>
                      <span className="reqcard__kicker">Ad request</span>
                    </div>
                    <div className="reqcard__status">{renderDesignStatusCell(row, row.status)}</div>
                  </div>

                  <div className="reqcard__field">
                    <span className="reqcard__label">Creative</span>
                    <div className="reqcard__creative">
                      <span className="reqcard__creative-name">{row.creative_name}</span>
                      {isPackage && (
                        <span className="reqcard__gift" title="Package Order">
                          <span>{packageIndex ? `${packageIndex}/${row.package_weeks}` : row.package_weeks}</span>
                          <sl-icon library="lucide" name="gift"></sl-icon>
                        </span>
                      )}
                      <sl-icon-button library="lucide" name="eye" onClick={() => { setRowData(row); setShowAdDetails(true); }}></sl-icon-button>
                    </div>
                  </div>

                  {row.client_name && (
                    <div className="reqcard__field">
                      <span className="reqcard__label">Client</span>
                      <span className="reqcard__client">{row.client_name}</span>
                    </div>
                  )}

                  <div className="reqcard__sales">
                    <span className="reqcard__label">Sales Person</span>
                    <div className="reqcard__sales-top">
                      <span className="reqcard__avatar">{initials}</span>
                      <div className="reqcard__sales-info">
                        <span className="reqcard__sales-name">{sp.sales_person_name || '-'}</span>
                        {sp.mobile && (
                          <span className="reqcard__sales-phone"><sl-icon library="lucide" name="phone"></sl-icon>{sp.mobile}</span>
                        )}
                      </div>
                      <MessageIcon row={row} />
                    </div>
                    {row.creative_start_date && (
                      <div className="reqcard__sales-dates">
                        <sl-icon library="lucide" name="calendar"></sl-icon>
                        <span>{start.dateStr} {start.timeStr} → {end.timeStr}</span>
                      </div>
                    )}
                  </div>

                  <div className="reqcard__grid">
                    <div className="reqcard__cell">
                      <span className="reqcard__label">CST Name</span>
                      <span className={`reqcard__value ${row.cst_name ? '' : 'reqcard__muted'}`}>{row.cst_name || 'Not set'}</span>
                    </div>
                    <div className="reqcard__cell">
                      <span className="reqcard__label">Created</span>
                      <span className="reqcard__value">{created.dateStr} · {created.timeStr}</span>
                    </div>
                    <div className="reqcard__cell">
                      <span className="reqcard__label">Ad Type</span>
                      <span className="reqcard__value reqcard__adtype">
                        {row.type && <span>{row.type}</span>}
                        {row.ad_creative_sub_type && <span className="reqcard__chip">{row.ad_creative_sub_type}</span>}
                      </span>
                    </div>
                    <div className="reqcard__cell">
                      <span className="reqcard__label">Mobile</span>
                      <span className="reqcard__value">{row.client_mobile || '-'}</span>
                    </div>
                    <div className="reqcard__cell">
                      <span className="reqcard__label">Location</span>
                      <span className="reqcard__value"><LocationDetails data={row} /></span>
                    </div>
                  </div>

                  <div className="reqcard__field">
                    <span className="reqcard__label">Designer</span>
                    <select
                      className="reqcard__select"
                      value={designerAssignments[row.creative_id] || ""}
                      onChange={(e) => setDesignerAssignments((prev) => ({ ...prev, [row.creative_id]: e.target.value }))}
                    >
                      <option value="">Select Designer</option>
                      {Designer.map((designer) => (
                        <option key={designer.designer_id} value={JSON.stringify({ id: designer.designer_id, name: designer.name })}>{designer.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="reqcard__field">
                    <span className="reqcard__label">Content Writer</span>
                    <select
                      className="reqcard__select"
                      value={contentWriterAssignments[row.creative_id] || ""}
                      onChange={(e) => setContentWriterAssignments((prev) => ({ ...prev, [row.creative_id]: e.target.value }))}
                    >
                      <option value="">Select Content Writer</option>
                      {(contentWriters || []).map((cw) => (
                        <option key={cw.value} value={JSON.stringify({ id: cw.value, name: cw.label })}>{cw.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="reqcard__foot">
                    <button className="reqcard__btn-secondary" onClick={() => { setRowData(row); setShowAdDetails(true); }}>View Details</button>
                    <button className="reqcard__btn-primary" onClick={() => handlePickClick(row)}>Pick Ad</button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500 py-6 italic">No data found.</div>
          )}
          {hasMore && !loader && <div ref={mobileSentinelRef} className="h-6 w-full" />}
        </div>

        <div className="mobHide w-full">
        <div className="overflow-auto">
          <table className="table table-bordered">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Sales Person Details</th>
                <th>Ad Creation Date</th>
                <th>Campaign Name</th>
                <th>Creative Type</th>
                <th>Location Details</th>
                <th>Assign to Designer</th>
                <th>Assign to Content Writer</th>
                <th>Action</th>
                <th>Ad Details</th>
              </tr>
            </thead>
            {loader && assignAds.length === 0 ? (
              <TableLoader colspan="12" />
            ) : assignAds?.length > 0 ? (
              assignAds.map((rawRow, idx) => {
                const liveCount = liveUnread[rawRow.creative_id];
                const row = liveCount != null ? { ...rawRow, unread_count: liveCount, unreadCount: liveCount } : rawRow;
                const created_at = alphaformatDateTime(row.created_at);
                return (
                  <tbody key={row.creative_id} ref={rowRef(rawRow.creative_id, 'desktop')}>
                    <tr className={`border-t ${idx %2 === 0 ? '' : 'bg-gray-100'}`}>
                      <td>
                        {renderSalesPersonCell(row.sales_person,row)}
                      </td>
                      <td>
                        <div className="flex items-start flex-col">
                          <p>created on : </p>
                          <div>{created_at.dateStr+' '+created_at.timeStr}</div>
                        </div>
                      </td>
                      <td>
                        <CreativeName row={row}/>
                      </td>
                      <td>
                        <div className="flex flex-col gap-1 justify-start items-start">
                          {row.type && <><span>{row.type}</span> <hr className="w-full border-gray-500 my-1" /></>}
                          {row.ad_creative_sub_type && <span>{row.ad_creative_sub_type}</span>}
                        </div>
                      </td>
                      <td>
                        <LocationDetails data={row}/>
                      </td>
                      <td>
                        <select
                          className="border p-1 rounded"
                          value={designerAssignments[row.creative_id] || ""}
                          onChange={(e) =>
                            setDesignerAssignments((prev) => ({
                              ...prev,
                              [row.creative_id]: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select Designer</option>
                          {Designer.map((designer) => (
                            <option key={designer.designer_id} value={JSON.stringify({ id: designer.designer_id, name: designer.name })}>
                              {designer.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="border p-1 rounded"
                          value={contentWriterAssignments[row.creative_id] || ""}
                          onChange={(e) =>
                            setContentWriterAssignments((prev) => ({
                              ...prev,
                              [row.creative_id]: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select Content Writer</option>
                          {(contentWriters || []).map((cw) => (
                            <option key={cw.value} value={JSON.stringify({ id: cw.value, name: cw.label })}>
                              {cw.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={() => handlePickClick(row)}>
                          Pick Ad
                        </button>
                      </td>
                      <td>
                        <button className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={()=>{setRowData(row); setShowAdDetails(true)}}>View Ad Details</button>
                      </td>
                    </tr>
                  </tbody>
                );
              })
            ) : (
              <NoDataFound colspan="12" />
            )}
          </table>
        </div>
        </div>
      </div>
      {hasMore && !loader && <div ref={sentinelRef} className="h-6 w-full" />}
      {loader && assignAds.length > 0 && <div className="flex justify-center py-3"><div className="spinner-border text-primary" role="status" /></div>}

      <ContentWriterAssignmentModal
        isOpen={pickModalOpen}
        onClose={() => {
          setPickModalOpen(false);
          setPickModalContext({ creative_id: null, content_id: null, cw: null, designer: null });
        }}
        onConfirm={handleConfirmPickWithDescription}
        contentWriters={[]}
        currentContentWriter={pickModalContext.cw ? { content_writer_id: pickModalContext.cw.id, name: pickModalContext.cw.name } : null}
        currentDescription={""}
        creativeId={pickModalContext.creative_id}
        content_id={pickModalContext.content_id}
        hideWriterSelect={true}
      />
      <ContentDrawer isOpen={showAdDetails} type="adDetails" rowData={rowData} onClose={()=>setShowAdDetails(!showAdDetails)} />
    </div>
  );
};

export default AssignToDesignerAds;